import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  searchContacts, revealContact, importContacts, ApiError, type SearchPerson,
} from '../api.js';
import { useRecords, Drawer, Err } from '../ui.js';
import { matchPersona, seniorityFromTitle } from '../lib/persona-match.js';
import { monogram, rampOf } from './people.js';

// Finding the buying committee at ONE account, in a window that does only that.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY IT LEFT THE RECORD PAGE.
//
// Kyle: "You have the contact sourcing convoluted with Account sourcing and
// research. I think I should be able to do a wizard approach on the account
// level. Each page is becoming a large wall of data versus a place to focus
// and execute."
//
// He was describing a real thing. This lived inside a fold called "Research",
// eighth in a stack of nine, sharing a pane with `Enrich account` — a DIFFERENT
// purchase, of a different kind of thing, priced per account rather than per
// person. Two jobs in one container, at the bottom of the page, behind a
// disclosure triangle. Sourcing people is not a footnote to reading a record;
// it is the work.
//
// ACCOUNT ENRICHMENT DELIBERATELY DID NOT COME WITH IT. Firmographics answer
// "is this account worth my time" and belong with the facts about the company.
// People answer "who do I write to". Putting them side by side is what made the
// old pane confusing, and moving both into one window would have moved the
// confusion rather than removed it.
//
// ─────────────────────────────────────────────────────────────────────────────
// THE ORDER IS THE POINT, AND EVERY STEP STATES ITS PRICE BEFORE IT RUNS.
//
//   1. who are you looking for   free
//   2. here is who is there      free — surnames still masked
//   3. add them                  free
//      ...and reveal addresses   ONE CREDIT PER PERSON
//
// Step 3 is deliberately two buttons, not one with a checkbox. Adding is free
// and reversible; buying an address is neither. The free action is the warm one
// — the brand rule is one warm CTA per view, and it goes to the path that costs
// nothing. A button that spends real money should never be the prettiest thing
// on screen.
//
// NOTHING IS WRITTEN UNTIL YOU ADD. The search returns candidates and stores
// none of them. Agents propose; Kyle promotes.

export function SourcePeople({ company, onClose, onChanged }: {
  company: { id: string; slug: string; name: string; url: string | null;
             contacts: { apollo_person_id?: unknown }[] };
  onClose: () => void;
  onChanged: () => void;
}) {
  const [people, setPeople] = useState<SearchPerson[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState<'personas' | 'broad' | 'custom'>('personas');
  const [customTitles, setCustomTitles] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // The workspace's own personas, so a candidate can be labelled with the seat
  // they'd fill. Same matcher the rest of the app uses — one definition of
  // "which persona is this", not a second one that drifts.
  const personas = useRecords<{ id: string; name: string; titles: unknown; seniorities: unknown }>(
    'personas');
  const personaOf = (title: string | null) => matchPersona(title, personas.rows ?? []);

  // Already on the account. Offering to add them again is offering to spend a
  // credit on somebody you have.
  const known = new Set(company.contacts.map((p) => p.apollo_person_id).filter(Boolean));

  // Only the picked people Apollo actually holds an address for. The footer
  // prices THIS, not the selection: charging for somebody Apollo cannot supply
  // is how an operator stops trusting the number beside the button.
  const pickedWithEmail = (people ?? [])
    .filter((p) => picked.has(p.apollo_id) && p.has_email).length;

  // The import endpoint refuses more than 25 in one call; refusing at the
  // 26th tick beats a 400 after the click.
  const AT_ONCE = 25;
  const capped = picked.size >= AT_ONCE;

  const matching = (people ?? []).filter((p) => personaOf(p.title)).length;

  // Open with the search already run. The window is opened by a button that
  // says "Find people" — landing on a second button with the same label is the
  // exact defect Kyle reported on the account record.
  useEffect(() => {
    if (company.url) void search(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function search(nextPage: number, force?: 'personas' | 'broad' | 'custom') {
    const m = force ?? mode;
    setBusy('search'); setError(null); setNote(null);
    try {
      const r = await searchContacts(company.slug, {
        page: nextPage,
        ...(m === 'broad' ? { broad: true } : {}),
        ...(m === 'custom'
          ? { titles: customTitles.split(',').map((t) => t.trim()).filter(Boolean) }
          : {}),
      });
      // Page one replaces; a later page appends. Replacing on "load more" is
      // the bug that makes a list of 60 look like a list of 25 forever.
      setPeople((prev) => (nextPage === 1 ? r.people : [...(prev ?? []), ...r.people]));
      setTotal(r.total);
      setPage(nextPage);
      if (nextPage === 1) setPicked(new Set());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  /**
   * Put the ticked people on the account.
   *
   * `withEmail` is the only difference between free and paid, and they are
   * deliberately two calls rather than one call with a flag: import makes no
   * Apollo request at all, while reveal is one request and one credit PER
   * PERSON. Serial, because a partial failure across parallel purchases is
   * unreadable afterwards, and because each one is real money.
   */
  async function add(withEmail: boolean) {
    const chosen = (people ?? []).filter((p) => picked.has(p.apollo_id));
    if (!chosen.length) return;
    setBusy('add'); setError(null); setNote(null);
    let revealed = 0, blank = 0;
    try {
      if (withEmail) {
        for (const p of chosen) {
          if (!p.has_email) continue;
          const r = await revealContact(company.slug, p.apollo_id);
          if (r.email_found) revealed++; else blank++;
        }
        // Anybody Apollo had no address for still belongs on the account — they
        // are part of the committee, just not reachable by mail yet.
        const noAddress = chosen.filter((p) => !p.has_email);
        if (noAddress.length) await importContacts(company.slug, noAddress);
        setNote(
          `${revealed} address${revealed === 1 ? '' : 'es'} found`
          + (blank ? ` · ${blank} Apollo had no address for — the credit is spent either way` : '')
          + (noAddress.length ? ` · ${noAddress.length} added without one` : ''),
        );
      } else {
        const r = await importContacts(company.slug, chosen);
        setNote(`${r.imported} added. No credits spent — get an address from their `
          + 'record when you are ready to write to them.');
      }
      setPicked(new Set());
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  // Apollo keys on the domain. Without one there is no search to run, so say
  // that here rather than letting the button return a 400 nobody can act on.
  if (!company.url) {
    return (
      <Drawer wide title={`Find people at ${company.name}`}
        subtitle="Blocked — Apollo looks people up by company domain" onClose={onClose}>
        <div className="srcblocked">
          <p>
            This account has no website on file, and every people search keys on the
            domain. Set it and this works.
          </p>
          <div className="actions">
            <Link className="mini" to={`/companies/${company.slug}`} onClick={onClose}>
              Set the website
            </Link>
            <span className="cost">free</span>
          </div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      wide
      title={`Find people at ${company.name}`}
      subtitle="Searching is free. Nothing enters the CRM until you add it."
      onClose={onClose}
      // The foot goes navy only once something is ticked — the single dark beat
      // on this surface, at the moment the next click costs money.
      footerTone={picked.size > 0 ? 'spend' : undefined}
      footer={
        picked.size > 0 ? (
          <>
            <span className="isum"><strong>{picked.size} selected</strong></span>
            {/* FREE IS THE WARM ONE. See the header comment — the primary
                action is the reversible one that costs nothing. */}
            <button className="primary" disabled={busy !== null} onClick={() => void add(false)}>
              {busy === 'add' ? 'Adding…' : `Add ${picked.size} to the account`}
            </button>
            <span className="cost">free</span>
            <button
              disabled={busy !== null || pickedWithEmail === 0}
              title={pickedWithEmail === 0
                ? 'Apollo holds no address for any of these'
                : undefined}
              onClick={() => void add(true)}
            >
              {`Add and reveal ${pickedWithEmail} address${pickedWithEmail === 1 ? '' : 'es'}`}
            </button>
            <span className="cost">
              {pickedWithEmail} credit{pickedWithEmail === 1 ? '' : 's'}
            </span>
          </>
        ) : (
          <span className="meta srcfoot">
            Tick whoever belongs on this account. Adding is free; an address costs
            one credit each.
          </span>
        )
      }
    >
      {/* ── STEP 1 · WHO ────────────────────────────────────────────────── */}
      <section className="srcstep">
        <h4>Who are you looking for?</h4>
        <div className="chiprow">
          {([
            ['personas', 'Your personas'],
            ['broad', 'Any senior title'],
            ['custom', 'A specific title…'],
          ] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={`schip ${mode === k ? 'on' : ''}`}
              disabled={busy !== null}
              onClick={() => {
                setMode(k);
                setPicked(new Set());
                // A mode with no argument yet can search immediately; "a
                // specific title" waits for the title.
                if (k === 'custom') setPeople(null);
                else void search(1, k);
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'custom' && (
          <label className="field">
            <span className="field-label">Titles to look for</span>
            <input
              value={customTitles}
              placeholder="VP Product, Controller, Head of Estimating"
              onChange={(e) => setCustomTitles(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && customTitles.trim()) void search(1); }}
            />
            <span className="fieldhint">
              Comma separated. Apollo matches loosely, so "Controller" also finds
              "Assistant Controller".
            </span>
          </label>
        )}

        {mode === 'custom' && (
          <div className="actions">
            <button disabled={busy !== null || !customTitles.trim()}
              onClick={() => void search(1)}>
              {busy === 'search' ? 'Searching…' : 'Search Apollo'}
            </button>
            <span className="cost">free · no credits</span>
          </div>
        )}
      </section>

      {/* ── STEP 2 · WHO IS THERE ───────────────────────────────────────── */}
      {busy === 'search' && !people && <p className="loading">Asking Apollo…</p>}

      {people && people.length === 0 && (
        <div className="emptysearch">
          <p className="meta">
            Nobody at {String(company.url).replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '') || 'this company'} matched{' '}
            {mode === 'broad' ? 'any senior title' : 'those titles'}.
          </p>
          {mode !== 'broad' && (
            <div className="actions">
              <button disabled={busy !== null}
                onClick={() => { setMode('broad'); void search(1, 'broad'); }}>
                Search every senior title instead
              </button>
              <span className="cost">still free</span>
              <Link className="mini" to="/icp" onClick={onClose}>Edit personas</Link>
            </div>
          )}
        </div>
      )}

      {people && people.length > 0 && (
        <section className="srcstep">
          {/* THE LINE THAT SAYS SOMETHING. Not "Results" — the count that
              decides whether this search was any good. The persona match is
              the single takeaway, so it carries the one amber node. */}
          <h4 className="srccount">
            <span>{total ?? people.length} at {company.name}</span>
            {matching > 0 && (
              <span className="srcmatch">
                <em>{matching}</em> match your personas
              </span>
            )}
          </h4>

          {people.map((p) => {
            const already = known.has(p.apollo_id);
            const name = `${p.first_name} ${p.last_name_obfuscated}`.trim();
            const persona = personaOf(p.title);
            return (
              <div
                key={p.apollo_id}
                className={`prow sel ${already ? 'isknown' : ''} `
                  + `${picked.has(p.apollo_id) ? 'picked' : ''}`}
              >
                {already ? (
                  <span className="tickbox off" title="Already on this account">✓</span>
                ) : (
                  <input
                    type="checkbox"
                    className="tick"
                    aria-label={`Select ${name}`}
                    checked={picked.has(p.apollo_id)}
                    disabled={busy !== null || (capped && !picked.has(p.apollo_id))}
                    onChange={(e) => setPicked((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(p.apollo_id);
                      else next.delete(p.apollo_id);
                      return next;
                    })}
                  />
                )}
                <span
                  className="av"
                  style={{ background: rampOf(p.seniority ?? seniorityFromTitle(p.title)) }}
                >
                  {monogram(name)}
                </span>
                <div className="who">
                  <strong>{name}</strong>
                  <span className="title">{p.title ?? '—'}</span>
                </div>
                <span className="chips">
                  {already
                    ? <span className="flag flag-ok">on the account</span>
                    : persona
                      ? <span className="pchip">{persona}</span>
                      : <span className="pchip none">no persona</span>}
                  <span className="sen">
                    {(p.seniority ?? seniorityFromTitle(p.title)).replace('_', ' ')}
                  </span>
                  {!p.has_email && (
                    <span className="flag flag-mute" title="Apollo holds no address for them">
                      no address
                    </span>
                  )}
                </span>
              </div>
            );
          })}

          {total !== null && total > people.length && (
            <div className="pager">
              <span>Showing {people.length} of {total}</span>
              <button className="mini" disabled={busy !== null}
                onClick={() => void search(page + 1)}>
                {busy === 'search' ? 'Loading…' : 'Load the next 25'}
              </button>
              <span className="cost">still free</span>
            </div>
          )}
        </section>
      )}

      {note && <p className="ok-note">{note}</p>}
      <Err>{error}</Err>
    </Drawer>
  );
}
